import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collaborationsApi } from '@/services/api';
import type { CreateCollaborationInput, UpdateCollaborationInput } from '@/types';

export const useCollaborations = (page = 1, limit = 20, filters?: { configId?: string; requestId?: string; status?: string; queueId?: string; search?: string; endpointId?: string; from?: string; to?: string; environment?: string; includeTests?: boolean; sortBy?: 'startedAt' | 'duration' | 'cost'; sortDir?: 'asc' | 'desc' }) => {
  return useQuery({
    queryKey: ['collaborations', page, limit, filters],
    queryFn: () => collaborationsApi.getAll(page, limit, filters).then(res => res.data),
  });
};

export const useCollaboration = (id: string | undefined) => {
  return useQuery({
    queryKey: ['collaboration', id],
    queryFn: () => collaborationsApi.getById(id!).then(res => res.data.data),
    enabled: !!id,
  });
};

export const useCollaborationJournals = (collaborationId: string | undefined, page = 1, limit = 20, filters?: { nodeType?: string; event?: string; agentId?: string; nodeId?: string }) => {
  return useQuery({
    queryKey: ['collaboration-journals', collaborationId, page, limit, filters],
    queryFn: () => collaborationsApi.getJournals(collaborationId!, page, limit, filters).then(res => res.data),
    enabled: !!collaborationId,
  });
};

export const useCollaborationStatuses = (collaborationId: string | undefined, page = 1, limit = 20, filters?: { status?: string }) => {
  return useQuery({
    queryKey: ['collaboration-statuses', collaborationId, page, limit, filters],
    queryFn: () => collaborationsApi.getStatuses(collaborationId!, page, limit, filters).then(res => res.data),
    enabled: !!collaborationId,
  });
};

export const useCollaborationNodeData = (collaborationId: string | undefined) => {
  return useQuery({
    queryKey: ['collaboration-node-data', collaborationId],
    queryFn: () => collaborationsApi.getNodeData(collaborationId!).then(res => res.data.data),
    enabled: !!collaborationId,
  });
};

export const useCreateCollaboration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCollaborationInput) =>
      collaborationsApi.create(data).then(res => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collaborations'] });
    },
  });
};

export const useUpdateCollaboration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCollaborationInput }) =>
      collaborationsApi.update(id, data).then(res => res.data.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collaborations'] });
      queryClient.invalidateQueries({ queryKey: ['collaboration', variables.id] });
    },
  });
};

